import React, { useEffect, useState, useRef } from 'react';
import { TradeJournalEntry, RuleSettings } from "../types";
import NumberField from "./NumberField";
import { useTranslation } from 'react-i18next';
import { Edit2, Trash2, XIcon } from 'lucide-react';

function Button(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
    const { className = "", ...rest } = props;
    return <button className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 shadow-sm hover:shadow transition-all ${className}`} {...rest} />
}

export default function PreEntryJournalForm({ dateISO, equity, settings, onSave, initial }: { dateISO: string; equity: number; settings: RuleSettings; onSave: (e: TradeJournalEntry) => void; initial?: any }) {
    
    const [entry, setEntry] = useState<number>(0);
    const [sl, setSL] = useState<number>(0);
    const [lots, setLots] = useState<number>(1);
    const [rr, setRR] = useState<number>(settings.minRRAllowed);
    const [valuePerPoint, setValuePerPoint] = useState<number>(1);
    const [newCondition, setNewCondition] = useState<string>('');
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [editValue, setEditValue] = useState<string>('');
    const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
    const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
    const editInputRef = useRef<HTMLInputElement>(null);
    const editValueRef = useRef<string>('');
    const { t } = useTranslation();
    const [conditions, setConditions] = useState<{ content: string; active: boolean }[]>([
        { content: t('behavioral_performance_daily_note_1'), active: true },
        { content: t('behavioral_performance_daily_note_2'), active: false },
        { content: t('behavioral_performance_daily_note_3'), active: false }
    ]);

    const riskPoints = Math.abs((entry || 0) - (sl || 0));
    const cashRisk = riskPoints * (valuePerPoint || 0) * (lots || 0);
    const profitAtTP = cashRisk * (rr || 0);
    const riskPct = equity ? (cashRisk / equity) * 100 : 0;
    const tpPrice = (entry && sl && rr) ? (entry > sl ? entry + rr * (entry - sl) : entry - rr * (sl - entry)) : NaN;

    const rrTooLow = rr > 0 && rr < settings.minRRAllowed;
    const riskTooHigh = riskPct > settings.maxRiskPercent;

    // Hydrate form from latest pre-entry when provided
    useEffect(() => {
        if (!initial || !initial.condition) return;
        try {
            const cond = initial.condition;
            // Normalize conditions: array of strings/objects -> [{content, active}]
            const raw = cond.conditions ?? cond.notes ?? cond.conditions ?? [];
            let normalized: { content: string; active: boolean }[] = [];
            if (Array.isArray(raw)) {
                if (raw.length > 0 && typeof raw[0] === 'string') {
                    normalized = (raw as string[]).filter(Boolean).map((s: string) => ({ content: String(s).trim(), active: true }));
                } else {
                    normalized = (raw as any[])
                        .map((c: any) => {
                            if (typeof c === 'string') return { content: c.trim(), active: true };
                            if (c && typeof c === 'object') return { content: String(c.content ?? '').trim(), active: Boolean(c.active) };
                            return undefined;
                        })
                        .filter((x: any): x is { content: string; active: boolean } => Boolean(x && x.content));
                }
            } else if (typeof raw === 'string') {
                normalized = raw.split(',').map((s: string) => s.trim()).filter(Boolean).map((s: string) => ({ content: s, active: true }));
            }

            if (normalized.length > 0) setConditions(normalized);
            if (Number.isFinite(Number(cond.entry))) setEntry(Number(cond.entry));
            if (Number.isFinite(Number(cond.sl))) setSL(Number(cond.sl));
            if (Number.isFinite(Number(cond.lots))) setLots(Number(cond.lots));
            if (Number.isFinite(Number(cond.rr))) setRR(Number(cond.rr));
            if (Number.isFinite(Number(cond.valuePerPoint))) setValuePerPoint(Number(cond.valuePerPoint));
        } catch (e) {
            // ignore hydration errors
        }
    }, [initial]);

    const Dialog: React.FC<{
        open: boolean;
        onClose: () => void;
        children: React.ReactNode;
    }> = ({ open, onClose, children }) => {
        if (!open) return null;
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000,
            }}>
                <div style={{
                    background: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    maxWidth: '600px',
                    width: '100%',
                    maxHeight: '80vh',
                    overflowY: 'auto',
                }}>
                    <button onClick={onClose} style={{ float: 'right', cursor: 'pointer' }}>
                        <XIcon></XIcon>
                    </button>
                    {children}
                </div>
            </div>
        );
    };

    const handleAddCondition = () => {
        if (newCondition.trim()) {
            setConditions([...conditions, { content: newCondition.trim(), active: false }]);
            setNewCondition('');
        }
    };

    const handleEditCondition = (index: number) => {
        setEditIndex(index);
        const content = conditions[index].content;
        setEditValue(content);
        editValueRef.current = content;
        setIsEditDialogOpen(true);
    };

    // Focus when edit dialog opens
    useEffect(() => {
        if (isEditDialogOpen && editInputRef.current) {
            // Small delay to ensure dialog is rendered
            setTimeout(() => {
                editInputRef.current?.focus();
            }, 100);
        }
    }, [isEditDialogOpen]);

    const handleSaveEdit = () => {
        const currentValue = editValueRef.current.trim();
        if (currentValue && editIndex !== null) {
            const updatedConditions = [...conditions];
            updatedConditions[editIndex] = { ...updatedConditions[editIndex], content: currentValue };
            setConditions(updatedConditions);
            setEditIndex(null);
            setEditValue('');
            editValueRef.current = '';
            setIsEditDialogOpen(false);
        }
    };

    const handleDeleteCondition = (index: number) => {
        setDeleteIndex(index);
        setIsDeleteDialogOpen(true);
    };

    const confirmDeleteCondition = () => {
        if (deleteIndex !== null) {
            setConditions(conditions.filter((_, i) => i !== deleteIndex));
            setDeleteIndex(null);
            setIsDeleteDialogOpen(false);
        }
    };

    const handleToggleActive = (index: number) => {
        setConditions(conditions.map((condition, i) =>
            i === index ? { ...condition, active: !condition.active } : condition
        ));
    };
    function save() {
        const id = `${dateISO}-${Date.now()}`;
        const payload: TradeJournalEntry = {
            id,
            createdAt: new Date().toISOString(),
            conditions,
            entry: Number(entry) || 0,
            lots: Number(lots) || 0,
            sl: Number(sl) || 0,
            rr: Number(rr) || 0,
            valuePerPoint: Number(valuePerPoint) || 0,
            equityAtEntry: equity,
            riskCash: cashRisk,
            riskPct,
            profitAtTP,
            lossAtSL: cashRisk,
            type: "pre_entry",
        };
        onSave(payload);
        // Reset minimal
        setConditions(conditions);
        setNewCondition('');
    }

    return (
        <div className="grid grid-cols-1 gap-3 text-sm">
            <div className="grid md:grid-cols-4 gap-3">
                <NumberField label={t('Entryprice')} value={entry} onChange={setEntry} step={0.01} />
                <NumberField label={t("Lots")} value={lots} onChange={setLots} step={0.01} />
                <NumberField label={t('StopLossprice')} value={sl} onChange={setSL} step={0.01} />
                <NumberField label={t("RRratio")} value={rr} onChange={setRR} step={0.1} />
            </div>

            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                    <span className="text-gray-700">{t('Entryfactors/conditions')}</span>
                    <div className="flex flex-col gap-2 max-h-40 overflow-y-auto">
                        {conditions.map((condition: { content: string; active: boolean }, index: number) => (
                            <div key={index} className="flex gap-2 items-center">
                                <input
                                    type="checkbox"
                                    checked={condition.active}
                                    onChange={() => handleToggleActive(index)}
                                    className="w-4 h-4 text-blue-600"
                                />
                                <span className="flex-1">{condition.content}</span>
                                <button
                                    className="!text-yellow-500 hover:!text-yellow-600"
                                    onClick={() => handleEditCondition(index)}
                                >
                                    <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                    className="!text-red-500 hover:!text-red-600"
                                    onClick={() => handleDeleteCondition(index)}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="flex flex-col gap-2">
                    <span className="text-gray-700">{t('AddCondition')}</span>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            className="rounded-md border border-gray-300 px-3 py-2 flex-1"
                            placeholder={t('EnterNewCondition')}
                            value={newCondition}
                            onChange={(e) => setNewCondition(e.target.value)}
                        />
                        <Button
                            className="bg-default-color bg-[#E9B460] hover:bg-[#d1a154] text-white !rounded-lg"
                            onClick={handleAddCondition}
                        >
                            {t('Add')}
                        </Button>
                    </div>
                </div>

                <Dialog open={isEditDialogOpen} onClose={() => setIsEditDialogOpen(false)}>
                    <div className="flex flex-col gap-3">
                        <span className="text-sm font-medium text-gray-900">{t('EditCondition')}</span>
                        <input
                            ref={editInputRef}
                            type="text"
                            className="rounded-md border border-gray-300 px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            defaultValue={editValue}
                            onInput={(e) => {
                                editValueRef.current = e.currentTarget.value;
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleSaveEdit();
                                } else if (e.key === 'Escape') {
                                    setIsEditDialogOpen(false);
                                }
                            }}
                        />
                        <div className="flex gap-2 justify-end">
                            <Button
                                className="bg-gray-200 hover:bg-gray-300 text-gray-800"
                                onClick={() => setIsEditDialogOpen(false)}
                            >
                                {t('Cancel')}
                            </Button>
                            <Button
                                className="bg-green-500 hover:bg-green-600 text-white"
                                onClick={handleSaveEdit}
                            >
                                {t('Save')}
                            </Button>
                        </div>
                    </div>
                </Dialog>

                <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
                    <div className="flex flex-col gap-2">
                        <span className="text-gray-700 font-medium">{t('DeleteCondition')}</span>
                        <p>{t('DeleteConditionConfirm')}</p>
                        <div className="flex gap-2 justify-end">
                            <Button
                                className="bg-gray-300 hover:bg-gray-400 text-white"
                                onClick={() => setIsDeleteDialogOpen(false)}
                            >
                                {t('Cancel')}
                            </Button>
                            <Button
                                className="bg-red-500 hover:bg-red-600 text-white"
                                onClick={confirmDeleteCondition}
                            >
                                {t('Delete')}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </div>

            <div className="grid md:grid-cols-4 gap-3">
                <NumberField label="Value per 1 point (per lot)" value={valuePerPoint} onChange={setValuePerPoint} step={0.01} />
                <div className="rounded-xl border border-gray-200 p-3 whitespace-nowrap">
                    <div className="text-xs text-gray-500">{t('RiskcashifSLhit')}</div>
                    <div className={`text-lg font-semibold ${riskTooHigh ? 'text-rose-600' : 'text-gray-900'}`}>{cashRisk.toFixed(2)}</div>
                    <div className="text-xs text-gray-500">≈ {riskPct.toFixed(2)}% {t('ofequity')}</div>
                </div>
                <div className="rounded-xl border border-gray-200 p-3">
                    <div className="text-xs text-gray-500">{t('ProfitatTP(RRrisk)')}</div>
                    <div className="text-lg font-semibold">{profitAtTP.toFixed(2)}</div>
                    <div className="text-xs text-gray-500">RR = {Number.isFinite(rr) ? rr.toFixed(2) : '—'}</div>
                    <div className="text-xs text-gray-500 mt-1">{t('TPprice')}: <span className="font-mono">{Number.isFinite(tpPrice) ? tpPrice.toFixed(5) : '—'}</span></div>
                </div>
                <div className="rounded-xl border border-gray-200 p-3">
                    <div className="text-xs text-gray-500">{t("Equity(reference)")}</div>
                    <div className="text-lg font-semibold">{equity.toFixed(2)}</div>
                    <div className="text-xs text-gray-500">{t('Fromdayopen')}</div>
                </div>
            </div>

            {(rrTooLow || riskTooHigh) && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-800 text-sm">
                    {rrTooLow && <div>⚠️ {t("CurrentRR")} ({rr.toFixed(2)}) {t('is')} <span className="font-semibold">{t('below')}</span> {t('theminimumrequired')} {settings.minRRAllowed}.</div>}
                    {riskTooHigh && <div>⚠️ {t('Estimatedrisk')} ~{riskPct.toFixed(2)}% {t('exceedsthemaximum')} {settings.maxRiskPercent}%.</div>}
                </div>
            )}

            <div className="flex items-center gap-3">
                <button onClick={save} className="px-4 sm:px-6 py-2 sm:py-3 bg-default-color bg-[#E9B460] hover:bg-[#d1a154] text-white !rounded-lg transition-colors duration-200 font-medium text-sm sm:text-base">
                    {t('Save')}
                </button>
                <span className="text-xs text-gray-500">{t("Time")}: {new Date().toLocaleTimeString()}</span>
            </div>
        </div>
    );
}